import { useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useMutation } from '@tanstack/react-query';
import { Lock, ArrowLeft, CheckCircle2, Loader2, AlertTriangle, Eye, EyeOff, XCircle } from 'lucide-react';
import { api } from '../lib/api';

export function ResetPassword() {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token') || '';
  const email = searchParams.get('email') || '';
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [success, setSuccess] = useState(false);

  const resetMutation = useMutation({
    mutationFn: async (newPassword: string) => {
      const response = await api.post('/auth/reset-password', {
        token,
        email,
        password: newPassword,
      });
      return response.data;
    },
    onSuccess: () => setSuccess(true),
  });

  // Missing params
  if (!token || !email) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-surface-950 px-4">
        <div className="w-full max-w-md glass rounded-2xl p-8 border border-surface-700 text-center">
          <XCircle className="w-12 h-12 text-red-400 mx-auto mb-4" />
          <h2 className="text-xl font-bold text-surface-100 mb-2">Link Inválido</h2>
          <p className="text-surface-400 mb-6">O link de recuperação é inválido ou está incompleto.</p>
          <Link to="/recovery" className="text-primary-400 hover:text-primary-300">
            Solicitar novo link
          </Link>
        </div>
      </div>
    );
  }

  // Success
  if (success) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-surface-950 px-4">
        <div className="w-full max-w-md glass rounded-2xl p-8 border border-surface-700 text-center">
          <CheckCircle2 className="w-12 h-12 text-green-400 mx-auto mb-4" />
          <h2 className="text-xl font-bold text-surface-100 mb-2">Senha Redefinida</h2>
          <p className="text-surface-400 mb-6">Sua senha foi alterada com sucesso. Faça login com a nova senha.</p>
          <Link
            to="/login"
            className="inline-flex items-center justify-center gap-2 px-6 py-3 bg-primary-500 text-white rounded-lg font-medium hover:bg-primary-600 transition-colors"
          >
            Ir para Login
          </Link>
        </div>
      </div>
    );
  }

  const passwordsMatch = password === confirmPassword;
  const passwordValid = password.length >= 8;
  const canSubmit = passwordValid && passwordsMatch && !resetMutation.isPending;

  return (
    <div className="min-h-screen flex items-center justify-center bg-surface-950 px-4">
      <div className="w-full max-w-md glass rounded-2xl p-8 border border-surface-700">
        <div className="text-center mb-6">
          <div className="w-16 h-16 rounded-full bg-primary-500/20 flex items-center justify-center mx-auto mb-4">
            <Lock className="w-8 h-8 text-primary-400" />
          </div>
          <h2 className="text-xl font-bold text-surface-100">Nova Senha</h2>
          <p className="text-surface-400 mt-2">Crie uma nova senha para sua conta.</p>
        </div>

        {resetMutation.isError && (
          <div className="mb-4 p-3 bg-red-500/10 border border-red-500/30 rounded-lg">
            <div className="flex items-center gap-2">
              <AlertTriangle className="w-4 h-4 text-red-400" />
              <span className="text-sm text-red-400">
                {(resetMutation.error as any)?.response?.data?.message || 'Token inválido ou expirado.'}
              </span>
            </div>
            <Link to="/recovery" className="text-primary-400 hover:text-primary-300 text-sm mt-2 inline-block">
              Solicitar novo link
            </Link>
          </div>
        )}

        <form
          onSubmit={(e) => {
            e.preventDefault();
            if (canSubmit) resetMutation.mutate(password);
          }}
          className="space-y-4"
        >
          <div>
            <label className="block text-sm font-medium text-surface-300 mb-1">Nova Senha</label>
            <div className="relative">
              <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-surface-500" />
              <input
                type={showPassword ? 'text' : 'password'}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Mínimo 8 caracteres"
                required
                minLength={8}
                className="w-full pl-10 pr-12 py-3 bg-surface-800 border border-surface-700 rounded-lg text-white placeholder-surface-500 focus:outline-none focus:ring-2 focus:ring-primary-500"
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-surface-500 hover:text-surface-300"
              >
                {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
              </button>
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-surface-300 mb-1">Confirmar Senha</label>
            <div className="relative">
              <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-surface-500" />
              <input
                type={showPassword ? 'text' : 'password'}
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                placeholder="Repita a senha"
                required
                className={`w-full pl-10 pr-4 py-3 bg-surface-800 border rounded-lg text-white placeholder-surface-500 focus:outline-none focus:ring-2 focus:ring-primary-500 ${
                  confirmPassword && !passwordsMatch ? 'border-red-500' : 'border-surface-700'
                }`}
              />
            </div>
            {confirmPassword && !passwordsMatch && (
              <p className="text-xs text-red-400 mt-1">As senhas não coincidem</p>
            )}
          </div>

          <button
            type="submit"
            disabled={!canSubmit}
            className="w-full py-3 bg-primary-500 text-white rounded-lg font-medium hover:bg-primary-600 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
          >
            {resetMutation.isPending ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin" />
                Redefinindo...
              </>
            ) : (
              'Redefinir Senha'
            )}
          </button>
        </form>

        <div className="mt-6 text-center">
          <Link
            to="/login"
            className="inline-flex items-center gap-2 text-surface-400 hover:text-surface-300 text-sm"
          >
            <ArrowLeft className="w-4 h-4" />
            Voltar ao Login
          </Link>
        </div>
      </div>
    </div>
  );
}

export default ResetPassword;
